import type {
  LegendColor,
  LegendName,
} from '~/modules/label-perf/types/label-perf';
import { formatNumberSuffix } from '~/modules/label-perf/utils/format';

export interface EnrichedDatum {
  seriesName: LegendName;
  rawValue: number;
  seriesColor: LegendColor;
  x: string | number;
  y: string | number;
  delta?: number;
  label?: string;
  date?: string;
}

interface ContentTooltipProps {
  datum?: EnrichedDatum;
  activePoints?: { datum: EnrichedDatum }[];
  index?: number;
  data?: EnrichedDatum[][];
  lineColors?: string[];
}

function ContentTooltip({
  datum,
  activePoints,
  index,
  data,
  lineColors,
}: ContentTooltipProps) {
  const currentPoint = datum || (activePoints && activePoints[0]?.datum);

  if (!currentPoint) {
    return null;
  }

  const displayDate = currentPoint.date || 'N/A';

  // Get all series values at current index
  const allSeriesData =
    data?.map((series, seriesIndex) => {
      const point = series[index ?? 0];

      return {
        seriesName: point?.seriesName || 'N/A',
        rawValue: point?.rawValue ?? 0,
        y: point?.y ?? 0,
        color: lineColors?.[seriesIndex] || point?.seriesColor || '#FFFFFF',
      };
    }) || [];

  return (
    <div
      data-testid="content-tooltip-wrapper"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
      }}
    >
      {/* Main Content Box */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          padding: '6px 8px',
          width: 'fit-content',
          backgroundColor: '#232323',
          color: 'white',
          border: '1px solid #FFFFFF1F',
          borderRadius: 4,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        }}
      >
        {/* Date Row */}
        <div
          style={{
            fontSize: 6,
            fontWeight: 600,
            borderBottom: '1px solid #FFFFFF1F',
            paddingBottom: 3,
            marginBottom: 1,
          }}
        >
          <span data-testid="tooltip-date-value">{displayDate}</span>
        </div>

        {/* All Series Data Rows */}
        {allSeriesData.map((series, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              fontSize: 5,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <div
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: series.color,
                }}
              />
              <span style={{ opacity: 0.8 }}>{series.seriesName}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontWeight: 600 }}>
                {Number(series.y).toFixed(1)}%
              </span>
              <span style={{ opacity: 0.6 }}>
                ({formatNumberSuffix(series.rawValue)})
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

ContentTooltip.displayName = 'ContentTooltip';

export { ContentTooltip };
